import type { MatchPhase } from "@livcg/schema";

export type Project = {
  id: string;
  slug: string;
  matchId: string;
  name: string;
  theme: string;
  status: string;
  kickoffAt: string | null;
};

export type MatchStatus = "live" | "scheduled" | "finished" | "undated";

export type Group<T extends Pick<Project, "kickoffAt"> = Project> = {
  key: MatchStatus;
  label: string;
  items: T[];
};

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
/** A match is over this long after kickoff even if nobody pressed FT. */
const MATCH_OVER_AFTER = 3 * HOUR;
/** An in-play phase counts as live only near its kickoff — a test project
 *  left at "1-taym" months ago is not a live match. */
const LIVE_NEAR_KICKOFF = 6 * HOUR;
/** "Nearby" window shown by default (user decision): results of the last
 *  7 days, fixtures of the next 14 days. Live matches always show. */
export const RECENT_RESULTS = 7 * DAY;
export const UPCOMING_FIXTURES = 14 * DAY;

export function kickoffMs(kickoffAt: string | null): number {
  return kickoffAt ? new Date(kickoffAt.replace(" ", "T")).getTime() : Number.NaN;
}

export function matchStatus(
  kickoffAt: string | null,
  phase: MatchPhase | undefined,
  now: number,
): MatchStatus {
  const t = kickoffMs(kickoffAt);
  const dated = !Number.isNaN(t);
  const inPlay = !!phase && phase !== "pre_match" && phase !== "full_time";
  if (inPlay && (!dated || Math.abs(now - t) < LIVE_NEAR_KICKOFF)) return "live";
  if (!dated) return "undated";
  if (phase === "full_time" || t < now - MATCH_OVER_AFTER) return "finished";
  return "scheduled";
}

const LABELS: Record<MatchStatus, string> = {
  live: "Jonli",
  scheduled: "Rejada",
  finished: "Natijalar",
  undated: "Sanasiz",
};
const ORDER: MatchStatus[] = ["live", "scheduled", "finished", "undated"];

function localDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Live (kickoff ↑) → Scheduled (kickoff ↑) → Results (date ↓, then time ↑)
 *  → Undated. Unless `all`, only the nearby window is kept (see RECENT_RESULTS /
 *  UPCOMING_FIXTURES; undated ones are hidden); `hidden` counts the rest. */
export function groupByStatus<T extends Pick<Project, "slug" | "name" | "kickoffAt">>(
  list: T[],
  phaseOf: (slug: string) => MatchPhase | undefined,
  now: number,
  opts: { all: boolean },
): { groups: Group<T>[]; hidden: number } {
  const buckets: Record<MatchStatus, T[]> = { live: [], scheduled: [], finished: [], undated: [] };
  let hidden = 0;
  for (const p of list) {
    const status = matchStatus(p.kickoffAt, phaseOf(p.slug), now);
    const t = kickoffMs(p.kickoffAt);
    const nearby =
      status === "live" ||
      (status === "scheduled" && t <= now + UPCOMING_FIXTURES) ||
      (status === "finished" && t >= now - RECENT_RESULTS);
    if (!opts.all && !nearby) {
      hidden++;
      continue;
    }
    buckets[status].push(p);
  }

  const byName = (a: T, b: T) => a.name.localeCompare(b.name);
  const asc = (a: T, b: T) => kickoffMs(a.kickoffAt) - kickoffMs(b.kickoffAt) || byName(a, b);
  buckets.live.sort(asc);
  buckets.scheduled.sort(asc);
  buckets.finished.sort((a, b) => {
    const ta = kickoffMs(a.kickoffAt);
    const tb = kickoffMs(b.kickoffAt);
    return localDay(tb) - localDay(ta) || ta - tb || byName(a, b);
  });
  buckets.undated.sort(byName);

  const groups = ORDER.map((key) => ({ key, label: LABELS[key], items: buckets[key] })).filter(
    (g) => g.items.length > 0,
  );
  return { groups, hidden };
}
